import { useEffect, useMemo, useState } from "react";
import { BodyText, Flex, Heading } from "@utilitywarehouse/hearth-react";
import { useNavigate } from "react-router-dom";

import { PATHS } from "../../config/routeConfig";
import NavigationButtons from "../../components/Questions/NavigationButtons";
import { useVulnerabilityContext } from "../../context/vulnerability/useVulnerabilityContext";
import { TemporaryNeedsQuestions } from "../../context/vulnerability/VulnerabilityContext";
import { buildTemporaryNeedsQuestions, checkIfPermanentNeeds } from "../utils";
import TemporaryNeedsQuestion from "../../components/Questions/TemporaryNeedsQuestion";
import { checkTokenExpiryOnInterval } from "../../context/auth/Token";
import { useAuthContext } from "../../context/auth/useAuthContext";
import Unauthenticated from "../../components/Unauthenticated";
import useTrackVulnerabilityJourney from "../../middleware/analytics/hooks/useTrackVulnerabilityJourney";

import { TemporaryNeedsDataTest } from "./constants";

export enum TemporaryNeedLabel {
  Bereavement = "bereavement in the last 90 days",
  YoungestChildAge = "child under 5",
  Pregnant = "pregnant",
  LivingAloneUnder18 = "living alone under 18",
  Hospital = "been in hospital in the last 30 days",
}

const TemporaryNeeds: React.FC = () => {
  const { answers, setAnswers } = useVulnerabilityContext();
  const { trackVulnerabilityJourneyOpen } = useTrackVulnerabilityJourney();
  const navigate = useNavigate();
  const [validBereavementDate, setValidBereavementDate] = useState(false);
  const [validYoungestChildAgeDate, setValidYoungestChildAgeDate] =
    useState(false);
  const [validHospitalDate, setValidHospitalDate] = useState(false);
  const [validLivingAloneDate, setValidLivingAloneDate] = useState(false);
  const { setAuthenticated, authenticated } = useAuthContext();

  const temporaryNeedsQuestions = useMemo((): TemporaryNeedsQuestions => {
    return buildTemporaryNeedsQuestions(answers);
  }, [answers]);

  useEffect(() => {
    trackVulnerabilityJourneyOpen(PATHS.TemporaryNeeds, `2 of 3`);
  }, []);

  const onChangeBereavementDate = (value: Date | null) => {
    setAnswers({
      ...answers,
      psr: {
        ...answers.psr,
        psrOutcome: {
          eligible: validateDates,
          optedIn: true,
        },
        psrTemporaryNeedsCodes: {
          ...answers.psr?.psrTemporaryNeedsCodes,
          bereavementDate: value,
        },
      },
    });
  };

  const onChangeYoungestChildAgeDate = (value: Date | null) => {
    setAnswers({
      ...answers,
      psr: {
        ...answers.psr,
        psrOutcome: {
          eligible: validateDates,
          optedIn: true,
        },
        psrTemporaryNeedsCodes: {
          ...answers.psr?.psrTemporaryNeedsCodes,
          youngestChildAgeDate: value,
        },
      },
    });
  };

  const onChangeHospitalDate = (value: Date | null) => {
    setAnswers({
      ...answers,
      psr: {
        ...answers.psr,
        psrOutcome: {
          eligible: validateDates,
          optedIn: true,
        },
        psrTemporaryNeedsCodes: {
          ...answers.psr?.psrTemporaryNeedsCodes,
          hospitalDate: value,
        },
      },
    });
  };

  const onChangeLivingAloneDate = (value: Date | null) => {
    setAnswers({
      ...answers,
      psr: {
        ...answers.psr,
        psrOutcome: {
          eligible: validateDates,
          optedIn: true,
        },
        psrTemporaryNeedsCodes: {
          ...answers.psr?.psrTemporaryNeedsCodes,
          livingAloneDate: value,
        },
      },
    });
  };

  const youngestChildLabel =
    temporaryNeedsQuestions?.childUnder5?.question === "When is your due date?"
      ? TemporaryNeedLabel.Pregnant
      : TemporaryNeedLabel.YoungestChildAge;

  const hasPermanentNeedCode = checkIfPermanentNeeds(answers);

  useEffect(() => {
    const now = new Date();
    const codes = answers.psr?.psrTemporaryNeedsCodes;

    // Bereavement Logic (Last 90 days)
    if (codes?.bereavementDate) {
      const bereavementDate = new Date(codes.bereavementDate);
      const ninetyDaysAgo = new Date(now);
      ninetyDaysAgo.setDate(now.getDate() - 90);

      setValidBereavementDate(
        !!codes.bereavementDate && bereavementDate > ninetyDaysAgo,
      );
    }

    // Hospital Logic (Last 30 days)
    if (codes?.hospitalDate) {
      const hospitalDate = new Date(codes.hospitalDate);
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);

      setValidHospitalDate(
        !!codes.hospitalDate && hospitalDate > thirtyDaysAgo,
      );
    }

    // Living Alone Logic (Born within the last 18 years)
    if (codes?.livingAloneDate) {
      const livingAloneDate = new Date(codes.livingAloneDate);
      const eighteenYearsAgo = new Date(now);
      eighteenYearsAgo.setFullYear(now.getFullYear() - 18);

      setValidLivingAloneDate(
        !!codes.livingAloneDate && livingAloneDate > eighteenYearsAgo,
      );
    }

    // Youngest Child Age Logic
    if (codes?.youngestChildAgeDate) {
      const childDate = new Date(codes.youngestChildAgeDate);

      if (answers.children?.childrenBetween2And4) {
        const fiveYearsAgo = new Date(now);
        fiveYearsAgo.setFullYear(now.getFullYear() - 5);
        const twoYearsAgo = new Date(now);
        twoYearsAgo.setFullYear(now.getFullYear() - 2);

        setValidYoungestChildAgeDate(
          !!codes.youngestChildAgeDate &&
            childDate > fiveYearsAgo &&
            childDate < twoYearsAgo,
        );
      }

      if (answers.children?.childrenUnder2) {
        const twoYearsAgo = new Date(now);
        twoYearsAgo.setFullYear(now.getFullYear() - 2);

        setValidYoungestChildAgeDate(
          !!codes.youngestChildAgeDate && childDate > twoYearsAgo,
        );
      }

      if (answers.children?.pregnant) {
        const nineMonthsFromNow = new Date(now);
        nineMonthsFromNow.setMonth(now.getMonth() + 9);

        setValidYoungestChildAgeDate(
          !!codes.youngestChildAgeDate &&
            childDate > now &&
            childDate < nineMonthsFromNow,
        );
      }
    }
  }, [answers.psr?.psrTemporaryNeedsCodes]);

  const validateDates =
    validBereavementDate ||
    validYoungestChildAgeDate ||
    validLivingAloneDate ||
    validHospitalDate;

  useEffect(() => {
    setAnswers({
      ...answers,
      psr: {
        ...answers.psr,
        psrOutcome: {
          eligible: validateDates,
          optedIn: true,
        },
        psrTemporaryNeedsCodes: {
          ...answers.psr?.psrTemporaryNeedsCodes,
        },
      },
    });
  }, [validateDates]);

  useEffect(() => {
    checkTokenExpiryOnInterval(authenticated, setAuthenticated);
  }, []);

  return (
    <Flex direction="column" marginTop="150">
      {!authenticated ? (
        <Unauthenticated />
      ) : (
        <Flex direction="column" gap="500">
          <Flex direction="column" spacing="lg">
            <Heading
              size="lg"
              data-testid={TemporaryNeedsDataTest.TEMPORARY_NEEDS_TITLE}
            >
              We need a bit more information...
            </Heading>

            <BodyText>
              Based on the information you have given us in the vulnerability
              assessment. We need more information to add you to PSR.
            </BodyText>
            <Flex
              direction="column"
              spacing="lg"
              data-testid={
                TemporaryNeedsDataTest.TEMPORARY_NEEDS_QUESTIONS_SECTION
              }
            >
              {temporaryNeedsQuestions.bereavement ? (
                <TemporaryNeedsQuestion
                  question={temporaryNeedsQuestions.bereavement.question}
                  reason={temporaryNeedsQuestions.bereavement.reason}
                  label={TemporaryNeedLabel.Bereavement}
                  onChangeDate={onChangeBereavementDate}
                  currentDate={
                    answers.psr?.psrTemporaryNeedsCodes?.bereavementDate
                  }
                />
              ) : null}

              {temporaryNeedsQuestions.childUnder5 ? (
                <TemporaryNeedsQuestion
                  question={temporaryNeedsQuestions.childUnder5.question}
                  reason={temporaryNeedsQuestions.childUnder5.reason}
                  label={youngestChildLabel}
                  onChangeDate={onChangeYoungestChildAgeDate}
                  currentDate={
                    answers.psr?.psrTemporaryNeedsCodes?.youngestChildAgeDate
                  }
                />
              ) : null}

              {temporaryNeedsQuestions.livingAloneUnder18 ? (
                <TemporaryNeedsQuestion
                  question={temporaryNeedsQuestions.livingAloneUnder18.question}
                  reason={temporaryNeedsQuestions.livingAloneUnder18.reason}
                  label={TemporaryNeedLabel.LivingAloneUnder18}
                  onChangeDate={onChangeLivingAloneDate}
                  currentDate={
                    answers.psr?.psrTemporaryNeedsCodes?.livingAloneDate
                  }
                />
              ) : null}

              {temporaryNeedsQuestions.beenInHospital ? (
                <TemporaryNeedsQuestion
                  question={temporaryNeedsQuestions.beenInHospital.question}
                  reason={temporaryNeedsQuestions.beenInHospital.reason}
                  label={TemporaryNeedLabel.Hospital}
                  onChangeDate={onChangeHospitalDate}
                  currentDate={
                    answers.psr?.psrTemporaryNeedsCodes?.hospitalDate
                  }
                />
              ) : null}
            </Flex>
          </Flex>
          <NavigationButtons
            onBack={() => navigate(-1)}
            onContinue={
              validateDates || hasPermanentNeedCode
                ? () => navigate(`/${PATHS.PSRServices}`)
                : () => navigate(`/${PATHS.PSRSummary}`)
            }
          />
        </Flex>
      )}
    </Flex>
  );
};

export default TemporaryNeeds;
